import React, { MouseEvent, useEffect, useState } from "react";

import { List, Menu, Tooltip } from "@mui/material";
import withStyles from "@mui/styles/withStyles";
import { FiFilter } from "react-icons/fi";

import { Category as ICategory, FilterBy as IFilterBy } from "../..";
import Button, { Color } from "../../components/Generic/Button";
import { getBrandCustom } from "../../utils/getCustomColor";
import Category from "./Category";
import useStyles from "./styles";
import { useTranslation } from "react-i18next";

export type Props = {
  filter: IFilterBy;
};

const ButtonCustomized = withStyles(() => ({
  root: {
    backgroundColor: getBrandCustom("primaryColor", "#308CDB"),
    borderRadius: "8px",
    height: "40px",
    padding: "10px",
    "& .MuiButton-startIcon": {
      marginLeft: 0,
    },
    "&:hover": {
      backgroundColor: getBrandCustom("primaryColor", "#369CF3"),
    },
    "&:active": {
      backgroundColor: getBrandCustom("primaryColor", "#03569E"),
    },
  },
}))(Button);

const FilterBy = (props: Props) => {
  const { filter, ...rest } = props;

  const classes = useStyles();
  const { t: translate } = useTranslation();

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  const [categories, setCategories] = useState<{ [x: string]: ICategory }>();
  const [actualCategories, setActualCategories] = useState<{
    [x: string]: ICategory;
  }>();

  const [filters, setFilters] = useState<{ [x: string]: string[] }>();

  useEffect(() => {
    if (anchorEl && Boolean(anchorEl)) setActualCategories(categories);
  }, [anchorEl]);

  useEffect(() => {
    if (filter?.categories) parserCategories(filter?.categories);
  }, [filter?.categories]);

  useEffect(() => {
    if (!filters && actualCategories) {
      const updatedCategories = getObjectFilter();

      setFilters(updatedCategories);
    }
  }, [actualCategories]);

  const parserCategories = (filterCategories: ICategory[]) => {
    const categoriesUpdated = { ...(categories || {}) };

    if (filterCategories && filterCategories?.length > 0) {
      filterCategories?.forEach((category) => {
        categoriesUpdated[category?.name] = {
          ...categoriesUpdated[category?.name],
          ...category,
        };
      });
    }
    setCategories(categoriesUpdated);
    setActualCategories(categoriesUpdated);
  };

  const addOrCreateInFilter = (
    category: ICategory,
    filters: { [x: string]: string[] },
    value: string
  ) => {
    const filtersUpdated: { [x: string]: string[] } = { ...filters };

    if (
      filtersUpdated[category?.column || category?.name?.toLowerCase()] &&
      Array.isArray(filtersUpdated[category?.column || category?.name?.toLowerCase()])
    ) {
      filtersUpdated[category?.column || category?.name?.toLowerCase()].push(value);
    } else {
      filtersUpdated[category?.column || category?.name?.toLowerCase()] = [value];
    }

    return filtersUpdated;
  };

  const getObjectFilter = () => {
    let filtersUpdated: { [x: string]: string[] } = {};

    if (actualCategories) {
      const keys = Object.getOwnPropertyNames(actualCategories);

      if (keys && keys?.length > 0) {
        keys?.map((key) => {
          const category = actualCategories[key];

          if (category) {
            if (
              category?.optionsSelected &&
              category?.optionsSelected?.length > 0 &&
              (category?.column || category?.name)
            ) {
              category?.optionsSelected?.forEach((optionSelected) => {
                filtersUpdated = addOrCreateInFilter(category, filtersUpdated, optionSelected);
              });
            }
          }

          return null;
        });
      }
    }

    return filtersUpdated;
  };

  const handleClick = (event: MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const onChangeCategory = (category: ICategory) => {
    const categoriesUpdated = { ...(actualCategories || {}) };

    categoriesUpdated[category?.name] = {
      ...categoriesUpdated[category?.name],
      ...category,
    };
    setActualCategories(categoriesUpdated);
  };

  const onSaveView = () => {
    handleClose();
    if (filter?.onChange) {
      const updatedCategories = getObjectFilter();

      filter?.onChange(updatedCategories);
      setCategories(actualCategories);
      setFilters(updatedCategories);
    }
  };

  const renderCategories = () => {
    if (actualCategories) {
      const keys = Object.getOwnPropertyNames(actualCategories);

      if (keys && keys?.length > 0) {
        return keys?.map((key) => {
          const category = actualCategories[key];

          if (
            (category && category?.options && category?.options?.length > 0) ||
            category?.onSearch
          )
            return (
              <Category key={category?.name} category={category} onChange={onChangeCategory} />
            );

          return null;
        });
      }
    }

    return null;
  };

  const getTotalOfFilters = () => {
    let count = 0;

    if (filters) {
      const keys = Object.getOwnPropertyNames(filters);

      if (keys && keys?.length > 0) {
        keys.map((key) => {
          if (filters && filters[key] && Array.isArray(filters[key])) {
            const filterArray = filters[key];

            count += filterArray.length;
          }

          return null;
        });
      }
    }

    return count;
  };

  const renderEndIcon = () => {
    if (filters && Object.getOwnPropertyNames(filters)?.length > 0) {
      return <div className={classes.badgeFilter}>{getTotalOfFilters()}</div>;
    }

    return null;
  };

  return (
    <>
      <Tooltip placement="top" title="Filters">
        <div>
          <ButtonCustomized
            startIcon={<FiFilter size={15} color="#FFFFFF" />}
            onClick={handleClick}
            style={{
              backgroundColor: anchorEl ? getBrandCustom("pressedColor", "#03569E") : undefined,
            }}
            endIcon={renderEndIcon()}
            {...rest}
          >
            <span className={classes.textButton}>{translate("filters")}</span>
          </ButtonCustomized>
        </div>
      </Tooltip>

      <Menu
        data-cy="menu-filter-by"
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={() => handleClose()}
        disableRestoreFocus
        PaperProps={{
          className: classes.menu,
        }}
        MenuListProps={{
          style: {
            padding: 0,
          },
        }}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
        transformOrigin={{ horizontal: "center", vertical: "top" }}
      >
        <div data-cy="container">
          <div data-cy="header" className={classes.menuHeader}>
            <span data-cy="title" className={classes.textHeader}>
              Filters
            </span>
            <Button data-cy="button-save" customColor={Color.SECONDARY} onClick={onSaveView}>
              Go
            </Button>
          </div>
          <List
            component="nav"
            data-cy="list-categories"
            className={classes.list}
            aria-labelledby="nested-list-subheader"
          >
            {renderCategories()}
          </List>
        </div>
      </Menu>
    </>
  );
};

export default FilterBy;
